/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICommerceApiSettings } from '../../contract/ICommerceApiSettings';
import { IGridSettings } from '../../contract/IGridSettings';
import { IAny, IGeneric } from '../../interfaces';
import { IImageData } from '../../interfaces/data-types/IImageData';
import { IImageDimension, IImageSettings } from '../../interfaces/data-types/IImageSettings';
import { Viewport } from './image-types/native-image';

const CROP_FOCAL_REGION = 'cropfocalregion';

const enum externalProviderName {
    sitecore = 'sitecore'
}

export const getLargestGridSettingViewportName = (gridSettings: IGridSettings): Viewport => {
    return (
        (gridSettings &&
            ((gridSettings.xl && 'xl') ||
                (gridSettings.lg && 'lg') ||
                (gridSettings.md && 'md') ||
                (gridSettings.sm && 'sm') ||
                (gridSettings.xs && 'xs'))) ||
        'xl'
    );
};

const formatMediaQuery = (max?: number, min?: number) =>
    `${min ? `(min-width:${min}px)${max ? ' and ' : ''}` : ''}${max ? `(max-width:${max}px)` : ''}`;

const getMedia = (
    gridSettings: IGridSettings,
    imageSettings: IImageSettings,
    viewport: Viewport,
    largestGridSettingViewportName: Viewport
) => {
    const { max, min } = getMinMaxWidth(gridSettings, imageSettings, viewport, largestGridSettingViewportName);
    return formatMediaQuery(max, min);
};

export const getMinMaxWidth = (
    gridSettings: IGridSettings,
    imageSettings: IImageSettings,
    viewport: Viewport,
    largestGridSettingViewportName: Viewport
): { max?: number; min?: number } => {
    const gridSetting = gridSettings[viewport];
    const max: number | undefined = gridSetting && gridSetting.w;
    if (!max) {
        return {};
    }
    // find first setting smaller than max
    const min = [
        imageSettings.viewports.xl && gridSettings.xl && gridSettings.xl.w,
        imageSettings.viewports.lg && gridSettings.lg && gridSettings.lg.w,
        imageSettings.viewports.md && gridSettings.md && gridSettings.md.w,
        imageSettings.viewports.sm && gridSettings.sm && gridSettings.sm.w,
        imageSettings.viewports.xs && gridSettings.xs && gridSettings.xs.w
    ]
        .filter(Boolean)
        .find(setting => setting && setting < max);
    return { max: viewport !== largestGridSettingViewportName ? max : undefined, min: min ? min + 1 : min };
};

export const getImageResizeUrl = (
    imageSrc: string | undefined,
    vpSetting: IImageDimension | undefined,
    inputImageSettings?: IImageSettings | undefined,
    skipWidthAndHeightSettings?: boolean,
    externalProvider?: string,
    apiSettings?: ICommerceApiSettings
): string => {
    if (!imageSrc) {
        return '';
    }
    if (imageSrc.startsWith('svg.')) {
        return imageSrc;
    }

    try {
        let queryString;

        if (isSitecoreCmsProviderUrl(externalProvider, apiSettings, imageSrc)) {
            imageSrc = santizeSitecoreImageUrl(apiSettings, imageSrc);
            if (vpSetting && vpSetting.qe) {
                queryString = vpSetting.qe;
            } else {
                queryString = '';
            }
        } else {
            if (vpSetting && vpSetting.q) {
                queryString = vpSetting.q;
            } else {
                queryString = 'm=6';

                if (vpSetting && vpSetting.w && vpSetting.h) {
                    queryString = `${queryString}&w=${vpSetting.w}&h=${vpSetting.h}`;
                }

                if (inputImageSettings && inputImageSettings.quality) {
                    queryString = `${queryString}&q=${inputImageSettings.quality}`;
                }
            }

            if (inputImageSettings && queryString && queryString.length > 0) {
                queryString = updateImageResizerQueryString(queryString, inputImageSettings);
            }
        }

        const srcUrl = new URL(imageSrc);
        if (srcUrl.search) {
            srcUrl.search += `&${queryString}`;
        } else {
            srcUrl.search += queryString;
        }

        if (skipWidthAndHeightSettings) {
            const params = srcUrl.searchParams;
            // remove w and h parameters from query string so the image is not adjusted with white background
            if (params.has('w')) {
                params.delete('w');
            }
            if (params.has('h')) {
                params.delete('h');
            }
        }
        return srcUrl.href;
    } catch (e) {
        return imageSrc;
    }
};

export const getSourceData = (
    src: string | undefined,
    gridSetting: IGridSettings,
    image: IImageData,
    imageSettings: IImageSettings,
    viewport: Viewport,
    largestGridSettingViewportName: Viewport,
    imageAttributes: IGeneric<IAny>
) => {
    if (!image || !src || !imageSettings || !imageSettings.viewports || !imageSettings.viewports[viewport]) {
        return;
    }

    const media = getMedia(gridSetting, imageSettings, viewport, largestGridSettingViewportName);
    if (!media) {
        return;
    }

    const srcSet = getImageResizeUrl(
        src,
        imageSettings.viewports[viewport],
        imageSettings,
        undefined,
        imageAttributes?.externalProvider,
        imageAttributes?.requestContext?.apiSettings
    );
    if (!srcSet) {
        return;
    }
    return {
        media,
        srcSet
    };
};

/**
 * This function reads the mode and background color values from ImageSettings and replaces the Image resizer query string
 * @param queryString ImageResizer query string
 * @param inputImageSettings Image Settings
 * @returns Updated ImageResizer query string with mode and background color
 */
const updateImageResizerQueryString = (queryString: string, inputImageSettings: IImageSettings): string => {
    const parsedQueryString = new URLSearchParams(queryString);
    if (inputImageSettings.mode) {
        parsedQueryString.set('m', inputImageSettings.mode.toString());
    }

    if (inputImageSettings.backgroundColor) {
        // Reading background color from site builder
        parsedQueryString.set('b', inputImageSettings.backgroundColor);

        // Converting image to jpg format, as background color in padding will only work if the image type is of format jpg,
        if (!parsedQueryString.has('f') || parsedQueryString.get('f') !== 'jpg') {
            parsedQueryString.set('f', 'jpg');
        }
    }

    if (inputImageSettings && inputImageSettings.cropFocalRegion) {
        parsedQueryString.set(CROP_FOCAL_REGION, 'true');
    }
    return parsedQueryString.toString();
};

/**
 * Function that checks if the product or category image url are from an external cms provider
 * @param externalProvider external provider string
 * @param apiSettings apiSettings
 * @returns Boolean
 */
const isSitecoreCmsProviderUrl = (
    externalProvider: string | undefined,
    apiSettings: ICommerceApiSettings | undefined,
    imageSrc: string
) => {
    const siteCoreBaseUrl = apiSettings?.externalCmsBaseUrls && apiSettings?.externalCmsBaseUrls[externalProviderName.sitecore];
    if (
        (externalProvider && externalProvider === externalProviderName.sitecore) ||
        (siteCoreBaseUrl && imageSrc.includes(siteCoreBaseUrl))
    ) {
        return true;
    } else {
        return false;
    }
};

const santizeSitecoreImageUrl = (apiSettings: ICommerceApiSettings | undefined, imageSrc: string) => {
    const siteCoreBaseUrl = apiSettings?.externalCmsBaseUrls && apiSettings?.externalCmsBaseUrls[externalProviderName.sitecore];
    let baseImageUrl = apiSettings?.baseImageUrl;
    if (siteCoreBaseUrl && imageSrc.includes(siteCoreBaseUrl) && siteCoreBaseUrl === baseImageUrl) {
        const imageUrl = decodeURIComponent(imageSrc.replace(baseImageUrl, ''));
        // Santize products image url to format sitecore accepts.
        const imageName = imageUrl.replace(/[^a-zA-Z\/\d_.\?=]/g, '!');
        baseImageUrl = validateBaseImageUrl(baseImageUrl, imageName);
        return baseImageUrl.concat(imageName);
    }
    return imageSrc;
};

/**
 * Function SSK uses to identify and construct sitecore image url
 * @param apiSettings apisettings
 * @param imageSrc imagesrc
 * @returns imageurl
 */
export const generateImageUrl = (apiSettings: ICommerceApiSettings, imageSrc: string | undefined) => {
    if (imageSrc) {
        if (imageSrc.startsWith('http')) {
            return imageSrc;
        }
        const baseImageUrl = validateBaseImageUrl(apiSettings.baseImageUrl, imageSrc);
        const imageUrl = baseImageUrl.concat(imageSrc);
        if (isSitecoreCmsProviderUrl(undefined, apiSettings, imageUrl)) {
            return santizeSitecoreImageUrl(apiSettings, imageUrl);
        } else {
            return baseImageUrl.concat(encodeURIComponent(imageSrc));
        }
    }
    return undefined;
};

/**
 * Function to generate product image url for Omni scenarios.
 * @param apiSettings apisettings
 * @param imageSrc imageSrc
 * @returns imageurl
 */
export const generateImageUrlForOmniChanel = (apiSettings: ICommerceApiSettings, imageSrc: string | undefined) => {
    if (imageSrc) {
        if (imageSrc.startsWith('http')) {
            return imageSrc;
        }
        if (!apiSettings.imageResizerEndPoint) {
            return undefined;
        }
        const baseImageUrl = validateBaseImageUrl(apiSettings.imageResizerEndPoint, imageSrc);

        const imageUrl = baseImageUrl.concat(imageSrc);
        if (isSitecoreCmsProviderUrl(undefined, apiSettings, imageUrl)) {
            return santizeSitecoreImageUrl(apiSettings, imageUrl);
        } else {
            return baseImageUrl.concat(encodeURIComponent(imageSrc));
        }
    }
    return undefined;
};

export const validateBaseImageUrl = (baseImageUrl: string, imageSrc: string) => {
    if (!baseImageUrl.endsWith('/') && !imageSrc.startsWith('/')) {
        return baseImageUrl.concat('/');
    }
    return baseImageUrl;
};
